import React, { memo, useCallback, useEffect, useRef } from 'react'
import { Alert, Form, Skeleton } from 'antd'
import { useSelector } from 'react-redux'
import {
  getMeasurementFormData,
  getMeasurementFormLoading,
  getVisibleFormData,
  getMeasurementFormError,
} from '../../selectors'
import { useDebounce } from 'src/react-app/hooks/useDebounce/useDebounce'
import { HStack, VStack } from '../../../../../ui/Stack'
import { DefaultJsonForm } from '../../../../../components/v2/default-json-form'
import { useSaveResult } from '../../hooks/useSaveResult'
import { useNavigate } from 'react-router-dom'

interface MeasurementFormsProps {
  className?: string
  id: string | number
}

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
}

export const MeasurementForms: React.FC<MeasurementFormsProps> = memo((props: MeasurementFormsProps) => {
  const { id } = props
  const [form] = Form.useForm()
  const measurementFormData = useSelector(getMeasurementFormData)
  const measurementFormIsLoading = useSelector(getMeasurementFormLoading)
  const measurementFormError = useSelector(getMeasurementFormError)
  const visibleFormData = useSelector(getVisibleFormData)
  const { saveResult } = useSaveResult()
  const navigate = useNavigate()
  const isInitialRender = useRef(true)

  const saveCase = useCallback(async (is_next_stage: boolean, is_done: boolean | undefined = undefined) => {
    await saveResult(id, visibleFormData, measurementFormData?.stages?.current, is_next_stage, is_done)
  }, [id, saveResult, visibleFormData, measurementFormData])

  const debouncedSaveCase = useDebounce(saveCase, 1000)

  useEffect(() => {
    const redirectUrl = measurementFormData?.redirectUrl
    if (redirectUrl) {
      navigate(redirectUrl)
    }
  }, [measurementFormData])

  useEffect(() => {
    if (visibleFormData) {
      isInitialRender.current = false
    }
  }, [visibleFormData])

  const onChangeForm = useCallback(
    ({formData}) => {
      if (isInitialRender.current) {
        return
      }
      if (!visibleFormData) {
        return
      }
      const isFilesEdit = JSON.stringify(formData) === JSON.stringify(visibleFormData?.resultObject)
      if (isFilesEdit) {
        return
      }
      visibleFormData.resultObject = {...formData}
      debouncedSaveCase(false, undefined)
    }, [debouncedSaveCase, visibleFormData]
  )

  if (measurementFormError && !measurementFormIsLoading && !measurementFormData) {
    return (
      <HStack gap={'8'} className={'mapWrapper'}>
        При выполнении запроса возникла ошибка.
        Попробуйте перезагрузить страницу или открыть карточку повторно.
      </HStack>
    )
  }

  return (
    <>
      <HStack gap={"8"} max>
        <Form
          {...formItemLayout}
          layout='horizontal'
          form={form}
          scrollToFirstError={true}
          style={{width: '100%'}}
        >
          {measurementFormError && !measurementFormIsLoading ? (
            <div className={'alertWrapper'}>
              <Alert
                message='При выполнении операции возникла ошибка:'
                showIcon
                type='error'
                description={measurementFormError}
              />
            </div>
          ) : null}
          {!measurementFormIsLoading ? (
            <VStack gap={'16'} max align={'stretch'} className={'appealsForm'}>
              {
                visibleFormData && (
                  <DefaultJsonForm
                    formData={{...visibleFormData.resultObject}}
                    schema={visibleFormData.resultSchema}
                    onChange={onChangeForm}
                    orientation='horizontal'
                  />
                )
              }
            </VStack>
          ) : (
            <Skeleton active/>
          )}
        </Form>
      </HStack>
    </>
  )
})
